import { useState, useCallback } from 'react'
import clsx from 'clsx'
import ViewSourcePopover from '@/matter-asset-common/references/ViewSourcePopover'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { useMatterAssetFeatureConfig, useMatterAssetStoreKey } from '@/matter-asset-common/feature/matter-asset-feature-context'
import type { MatterAssetEventFieldItem } from '@/matter-asset-common/feature/matter-asset-feature-config'
import type { CellPosition, ScrollToCell } from '@/medical-common/hooks/use-table-cell-scroll'

interface ReferencedTextItemProps {
  item: MatterAssetEventFieldItem
  cellPosition: CellPosition
  scrollToCell: ScrollToCell
  isFirst?: boolean
  bulleted?: boolean
  isCellHovered?: boolean
}

/**
 * Referenced Text Item
 *
 * Displays a single referenced text field with optional bullet styling.
 * Wrapped with ViewSourcePopover to enable the popover on:
 * - item-level hover, or
 * - cell-level hover for the first item in the cell
 */
export default function ReferencedTextItem(props: ReferencedTextItemProps) {
  const { item, cellPosition, scrollToCell, isFirst = false, bulleted, isCellHovered = false } = props
  const config = useMatterAssetFeatureConfig()
  const storeKey = useMatterAssetStoreKey()
  const [isHighlighted, setIsHighlighted] = useState(false)

  const reference = useAppSelector((state: RootState) => config.selectReferenceById(state, { storeKey, referenceId: item.reference_id }))
  // Check if the reference is currently open in the content viewer
  const isActive = useAppSelector((state: RootState) => config.selectIsReferenceActive(state, { referenceId: item.reference_id }))

  // Memoize the highlight change callback to prevent unnecessary re-renders in ViewSourcePopover
  const handleItemHighlightChange = useCallback((isHighlighted: boolean) => {
    setIsHighlighted(isHighlighted)
  }, [])

  // Polymorphic tag: 'li' for bulleted lists, 'div' for single field
  const Tag: 'li' | 'div' = bulleted ? 'li' : 'div'

  // If no reference found, render static text
  if (!reference) {
    return bulleted ? (
      <li className="flex items-start -ml-4 rounded-sm pr-1">
        <span className="inline-block w-4 text-center flex-shrink-0">•</span>
        <span className="text-sm">{item.text}</span>
      </li>
    ) : (
      <div className="inline">
        <span className="text-sm rounded-sm px-1 py-0.5 inline break-words box-decoration-clone">{item.text}</span>
      </div>
    )
  }

  // If we have a reference, render the interactive element
  return (
    <ViewSourcePopover
      reference={reference}
      cellPosition={cellPosition}
      scrollToCell={scrollToCell}
      isCellHovered={isCellHovered}
      allowCellHover={isFirst}
      onItemHighlightChange={handleItemHighlightChange}
      isReferenceActive={isActive}
    >
      <Tag
        className={clsx(
          'relative group pointer-events-auto',
          isActive ? 'cursor-default' : 'cursor-pointer',
          bulleted && 'flex items-start -ml-4 rounded-sm pr-1 my-0.5',
          bulleted && isHighlighted && 'bg-brand-neutral-100',
          bulleted && isActive && 'bg-yellow-100'
        )}
      >
        {/* Bullet for list items */}
        {bulleted && <span className="inline-block w-4 text-center flex-shrink-0">•</span>}

        {/* Text content */}
        {bulleted ? (
          <span className="text-sm">{item.text}</span>
        ) : (
          <span
            className={clsx(
              'text-sm rounded-sm px-1 py-0.5 inline break-words box-decoration-clone',
              isHighlighted && 'bg-brand-neutral-100',
              isActive && 'bg-yellow-100'
            )}
          >
            {item.text}
          </span>
        )}
      </Tag>
    </ViewSourcePopover>
  )
}
